import React, { useEffect, useRef } from 'react';
import '../Pages/styles.css'; // Import the prefixed CSS as requested

// --- Import FontAwesomeIcon Component ---
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

// --- Import ALL Necessary Icons from Font Awesome (Updated for Audit Content) ---
import {
    // Core Concepts & General
    faSearch,           // Audit / Examination / Review
    faClipboardCheck,   // Compliance / Checklist / Verification
    faBalanceScale,     // Integrity / Compliance / Legal
    faFileInvoiceDollar,// Financial Records / Accountability
    faShieldAlt,        // Risk Mitigation / Protection / Safety
    faTasks,            // Operational Efficiency / Processes
    faHeartPulse,       // Patient Care / Safety
    faUserTie,          // Expertise / Consultants
    faBinoculars,       // Monitoring / Follow-Up / Oversight
    faSyncAlt,          // Ongoing Support / Continuous Improvement
    faLightbulb,        // Recommendations / Insights / Innovation
    faCheckCircle,      // Success / Completion / Standards
    faExclamationTriangle, // Identifying Risks / Issues
    // Audit Process Steps
    faFileSignature,    // Preparation / Documentation / SOPs
    faPlayCircle,       // Execution / Start
    faClipboardList,    // Reporting / Findings
    faCogs,             // Implementation / Actionable Steps
    // Benefits
    // faFileInvoiceDollar reused for Financial Transparency
    // faTasks reused for Operational Efficiency
    // faBalanceScale reused for Regulatory Compliance
    // faExclamationTriangle reused for Risk Identification
    // faSyncAlt reused for Continuous Improvement
    // Keep other potentially useful icons if needed
    // Icons likely unused from previous versions:
    /*
     faCalculator, faMoneyCheckAlt, faExchangeAlt, faChartPie, faReceipt, faArrowsAltH,
     faUsersCog, faFileExcel, faTachometerAlt, faLandmark, faProjectDiagram, faMapSigns,
     faBullseye, faSitemap, faLaptopCode, faGavel, faHandHoldingDollar, faExpandArrowsAlt,
     faTrophy, faThumbsUp, faBuilding, faMedal
    */
} from '@fortawesome/free-solid-svg-icons'; // Using free solid icons


// --- Intersection Observer Hooks (No changes needed in hook logic) ---
const useIntersectionObserverTitle = (options) => {
    const containerRef = useRef(null);
    const [isVisible, setIsVisible] = React.useState(false);
    const callbackFunction = (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting) setIsVisible(true);
    };
    useEffect(() => {
        const observer = new IntersectionObserver(callbackFunction, options);
        const currentRef = containerRef.current;
        if (currentRef) observer.observe(currentRef);
        return () => { if (currentRef) observer.unobserve(currentRef); };
    }, [containerRef, options]);
    return [containerRef, isVisible];
};

const useIntersectionObserverElements = (options) => {
    useEffect(() => {
        const observerCallback = (entries, observer) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    const target = entry.target;
                    const parent = target.parentElement;
                    const siblings = parent ? Array.from(parent.children).filter(el => el.classList.contains('cliniqor-fade-in-up')) : [target];
                    const index = siblings.indexOf(target);
                    const staggerIndex = index >= 0 ? index : 0;

                    target.style.setProperty('--item-index', staggerIndex % 8); // Keep stagger logic
                    target.classList.add('cliniqor-visible'); // Use prefixed class
                    observer.unobserve(target);
                }
            });
        };
        const observer = new IntersectionObserver(observerCallback, options);
        const targets = document.querySelectorAll('.cliniqor-fade-in-up');
        targets.forEach(target => observer.observe(target));
        return () => targets.forEach(target => { if (target) observer.unobserve(target); });
    }, [options]);
};
// --- End Observer Hooks ---


function App() {

    // --- Observer Setup for Titles (Adjusted Refs) ---
    const titleObserverOptions = { threshold: 0.2 };
    const [title1Ref, title1Visible] = useIntersectionObserverTitle(titleObserverOptions); // Why Audits Important?
    const [title2Ref, title2Visible] = useIntersectionObserverTitle(titleObserverOptions); // Cliniqor's Approach
    const [title3Ref, title3Visible] = useIntersectionObserverTitle(titleObserverOptions); // Benefits of Audits

    // --- Observer Setup for General Fade-In Elements ---
    useIntersectionObserverElements({ threshold: 0.1 });

    // Choose a relevant hero background image for audit/compliance/finance
    const heroBgImage = 'https://images.unsplash.com/photo-1584713503693-bb386ec95cf2?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1920&q=80'; // Image suggesting review/scrutiny

  return(
    // Use prefixed base wrapper class
    <div className="cliniqor-app-wrapper">

      {/* --- HERO SECTION --- */}
      <header
        className="cliniqor-hero-section" // Prefixed
        style={{ backgroundImage: `url(${heroBgImage})` }}
      >
        <div className="cliniqor-container"> {/* Prefixed */}
          <div className="cliniqor-hero-content"> {/* Prefixed */}
            {/* --- NEW HERO CONTENT --- */}
            <h1>Internal & External Audit Services for Healthcare</h1>
            <p className="cliniqor-sub-heading">
              Ensuring Efficiency, Compliance, and Quality Care with Cliniqor
            </p>
            <p className="cliniqor-intro-text">
              Vital mechanisms for safeguarding integrity, ensuring financial sustainability, and optimizing performance. Cliniqor provides comprehensive audit services highlighting areas for improvement and enhancing patient care.
            </p>
            {/* --- END NEW HERO CONTENT --- */}
          </div>
        </div>
      </header>

      <main>
        {/* --- INTRO (Difference & Scope) --- */}
        <section className="cliniqor-section cliniqor-section-first cliniqor-section-bg-gradient cliniqor-intro-section">
          <div className="cliniqor-container">
             <div className="cliniqor-intro-grid">
                 <div className="cliniqor-intro-text-content cliniqor-fade-in-up">
                     {/* --- NEW INTRO CONTENT --- */}
                     <h3>More Than Formality: An In-Depth Examination</h3>
                     <p>Audits examine processes, procedures, and practices affecting financial health, service quality, staff performance, patient care, billing, and compliance. They identify gaps and inefficiencies for streamlined operations.</p>
                     <p>Internal audits (by internal teams, ongoing) focus on efficiency and policy adherence. External audits (by third parties, annual) provide unbiased assessments of financial health, compliance, and operational performance.</p>
                     {/* --- END NEW INTRO CONTENT --- */}
                 </div>
             </div>
          </div>
        </section>

        {/* --- WHY AUDITS ARE IMPORTANT (Features Section - 4 items) --- */}
        <section className="cliniqor-section cliniqor-features-section cliniqor-section-bg-light">
          <div className="cliniqor-container">
            <div ref={title1Ref} className={`cliniqor-section-title cliniqor-fade-in-up ${title1Visible ? 'cliniqor-visible' : ''}`}>
              {/* --- NEW SECTION TITLE --- */}
              <h2>Why Are Audits Important for Healthcare Systems?</h2>
              <p>Essential purposes benefiting hospitals, patients, and stakeholders.</p>
              {/* --- END NEW SECTION TITLE --- */}
            </div>
            <div className="cliniqor-features-grid"> {/* Grid structure maintained (4 items) */}
              {/* --- NEW FEATURE CARDS --- */}
              <div className="cliniqor-feature-card cliniqor-fade-in-up">
                <div className="cliniqor-feature-card-content">
                    <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faBalanceScale} /></div>
                    <h4>1. Compliance with Regulatory Requirements</h4>
                    <p>Ensuring adherence to evolving legal, financial, and operational rules to mitigate risks.</p>
                </div>
              </div>
              <div className="cliniqor-feature-card cliniqor-fade-in-up">
                 <div className="cliniqor-feature-card-content">
                    <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faFileInvoiceDollar} /></div>
                    <h4>2. Financial Accountability & Transparency</h4>
                    <p>Assuring appropriate fund use, preventing fraud, ensuring accuracy in financial records.</p>
                </div>
              </div>
              <div className="cliniqor-feature-card cliniqor-fade-in-up">
                 <div className="cliniqor-feature-card-content">
                    <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faShieldAlt} /></div> {/* Combined Risk & Efficiency */}
                    <h4>3. Risk Mitigation & Operational Efficiency</h4>
                    <p>Identifying threats early, taking corrective actions, optimizing processes, improving resource allocation.</p>
                </div>
              </div>
               <div className="cliniqor-feature-card cliniqor-fade-in-up">
                 <div className="cliniqor-feature-card-content">
                    <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faHeartPulse} /></div>
                    <h4>4. Improving Patient Care and Safety</h4>
                    <p>Reviewing clinical practices and safety protocols to ensure adherence to best practices.</p>
                </div>
              </div>
              {/* --- END NEW FEATURE CARDS --- */}
            </div>
          </div>
        </section>

        {/* --- CLINIQOR'S APPROACH (Detailed Planning Section - 5 steps) --- */}
        <section className="cliniqor-section cliniqor-section-bg-texture cliniqor-detailed-planning-section"> {/* Kept existing classes */}
          <div className="cliniqor-container">
             <div ref={title2Ref} className={`cliniqor-section-title cliniqor-fade-in-up ${title2Visible ? 'cliniqor-visible' : ''}`}>
              {/* --- NEW SECTION TITLE --- */}
              <h2>Cliniqor's Approach to Internal and External Audits</h2>
               <p>A comprehensive suite ensuring efficiency, compliance, and sustainability.</p>
               {/* --- END NEW SECTION TITLE --- */}
            </div>
            {/* Using Planning Items for the 5 Approach Steps */}
            <div className="cliniqor-planning-items-wrapper">
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faFileSignature} /></div>
                    <div className="cliniqor-content"><h4>1. Audit Preparation</h4> <p>Designing tailored framework, training personnel, gathering documentation, refining SOPs, ensuring compliance.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faPlayCircle} /></div>
                    <div className="cliniqor-content"><h4>2. Audit Execution</h4> <p>Thorough review of financials, operations, clinical practices, compliance via interviews, evaluations, documentation review.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faLightbulb} /></div>
                    <div className="cliniqor-content"><h4>3. Recommendations and Implementation</h4> <p>Providing detailed reports with actionable recommendations, assisting with effective implementation.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faBinoculars} /></div>
                    <div className="cliniqor-content"><h4>4. Follow-Up Audits</h4> <p>Tracking progress, measuring effectiveness of changes, ensuring goals are met, addressing remaining gaps.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faSyncAlt} /></div>
                    <div className="cliniqor-content"><h4>5. Ongoing Support and Monitoring</h4> <p>Continuous advice on practices, financial transparency, patient care optimization, staff training.</p> </div>
                 </div>
            </div>
          </div>
        </section>

        {/* --- BENEFITS OF AUDITS (Features Section - Reused) --- */}
        <section className="cliniqor-section cliniqor-features-section cliniqor-section-bg-light">
           <div className="cliniqor-container">
              <div ref={title3Ref} className={`cliniqor-section-title cliniqor-fade-in-up ${title3Visible ? 'cliniqor-visible' : ''}`}>
                 <h2>Benefits of Internal & External Audits for Hospitals</h2>
                 <p>Extending far beyond regulatory compliance.</p>
              </div>
              <div className="cliniqor-features-grid">
                    {/* --- Cards for Benefits --- */}
                    <div className="cliniqor-feature-card cliniqor-fade-in-up">
                        <div className="cliniqor-feature-card-content">
                            <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faFileInvoiceDollar} /></div>
                            <h4>1. Financial Transparency & Integrity</h4>
                            <p>Ensuring accurate reflection of financial status, building trust, preventing fraud.</p>
                        </div>
                    </div>
                    <div className="cliniqor-feature-card cliniqor-fade-in-up">
                        <div className="cliniqor-feature-card-content">
                            <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faTasks} /></div>
                            <h4>2. Increased Operational Efficiency</h4>
                            <p>Highlighting inefficiencies, streamlining processes, reducing waste, optimizing resources.</p>
                        </div>
                    </div>
                    <div className="cliniqor-feature-card cliniqor-fade-in-up">
                        <div className="cliniqor-feature-card-content">
                            <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faBalanceScale} /></div>
                            <h4>3. Regulatory Compliance</h4>
                            <p>Ensuring adherence to all relevant standards, protecting from legal/financial risks.</p>
                        </div>
                    </div>
                    <div className="cliniqor-feature-card cliniqor-fade-in-up">
                        <div className="cliniqor-feature-card-content">
                            <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faExclamationTriangle} /></div>
                            <h4>4. Risk Identification & Mitigation</h4>
                            <p>Proactively identifying financial, operational, or regulatory risks before escalation.</p>
                        </div>
                    </div>
                     <div className="cliniqor-feature-card cliniqor-fade-in-up">
                        <div className="cliniqor-feature-card-content">
                            <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faSyncAlt} /></div>
                            <h4>5. Continuous Improvement & Innovation</h4>
                            <p>Acting as a catalyst for evolving practices, enhancing care, staying competitive.</p>
                        </div>
                    </div>
               </div>
           </div>
        </section>

       {/* No separate Conclusion section, integrated into Footer */}

      </main>

      {/* --- FOOTER CTA --- */}
      <footer className="cliniqor-section cliniqor-section-narrow cliniqor-footer-cta">
        <div className="cliniqor-container cliniqor-fade-in-up">
          {/* --- UPDATED FOOTER CONTENT --- */}
          <h2>Ensure Operational Excellence with Expert Audits</h2>
          <p>Internal and external audits are indispensable. Cliniqor provides expert guidance throughout the audit process, helping improve operations, financial management, and patient care for long-term success and sustainability.</p>
          <a href="#contact" className="cliniqor-cta-button">Request Audit Consultation</a>
          {/* --- END UPDATED FOOTER CONTENT --- */}
        </div>
      </footer>
    </div> // End cliniqor-app-wrapper
  );
}

export default App;